import { useState, useEffect } from "react";
import { Input, Card } from "antd";
import StandardButton from '../components/StandardButton';
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Collapse, Typography } from 'antd';

const HISTORY_KEY = "audioQuizHistory";

export default function AudioQuiz() {
  const { t } = useTranslation();
  const [topic, setTopic] = useState(t("audio_quiz_input_placeholder"));
  const [loading, setLoading] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [history, setHistory] = useState([]);
  const [analyticsMap, setAnalyticsMap] = useState({});

  useEffect(() => {
    const fetchAllQuizzes = async () => {
      try {
        const res = await api.get("/audio-quiz/all");
        const all = res.data.quizzes || [];
        setHistory(all);

        all.forEach(async (item) => {
          try {
            const res = await api.get(`/audio-quiz/analytics/${item.id}`);
            setAnalyticsMap(prev => ({ ...prev, [item.id]: res.data }));
          } catch (_) {
          }
        });
      } catch (err) {
        toast.error(t("load_quiz_history_failed"));
      }
    };

    fetchAllQuizzes();
  }, []);


  const saveToHistory = (id, topic, quizTitle) => {
    const newEntry = {
      id,
      topic,
      title: quizTitle,
      timestamp: new Date().toISOString(),
    };
    const updatedHistory = [newEntry, ...history];
    setHistory(updatedHistory);
  };

  const generateQuiz = async () => {
    const loadingToast = toast.loading(t("audio_quiz_generating"));
    setLoading(true);
    try {
      const res = await api.post("/audio-quiz/generate", { topic });

      const share = await api.post("/audio-quiz/share", { quiz: res.data.quiz, prompt: topic });

      setShareId(share.data.id);
      saveToHistory(share.data.id, topic, res.data.quiz.title);
      toast.success(t("audio_quiz_generation_success"), { id: loadingToast });
    } catch (err) {
      toast.error(t("audio_quiz_generation_error"), { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = () => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <main>
        <Card className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="space-y-4">
            <Input.TextArea
              placeholder={t("audio_quiz_input_placeholder")}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              size="large"
              autoSize={{ minRows: 4, maxRows: 10 }} // Increased minRows for multiline
            />
            <StandardButton type="secondary" onClick={generateQuiz} loading={loading}>
              {t("generate_button")}
            </StandardButton>

            {shareId && (
              <>
                <div className="mt-6">
                  <p className="font-semibold mb-2">{t("audio_quiz_preview_title")}:</p>
                  <iframe
                    src={`/tools/audio-quiz/${shareId}?embed=true`}
                    className="w-full h-[500px] border rounded-md shadow"
                    title="Quiz Preview"
                  />
                </div>
                <div className="mt-4">
                  <StandardButton
                    type="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/tools/audio-quiz/${shareId}`);
                      toast.success(t("quiz_link_copied"));
                    }}
                  >
                    {t("audio_quiz_copy_link")}
                  </StandardButton>
                </div>
              </>
            )}
          </div>

          {history.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-semibold mb-4">{t("history")}</h3>
              <Collapse accordion>
                {history.map((item, index) => (
                  <Collapse.Panel
                    key={index}
                    header={
                      <div className="flex flex-col">
                        <Typography.Text strong className="text-base">{item.title || `Quiz ${index + 1}`}</Typography.Text>
                        <span className="text-xs text-gray-500">{new Date(item.timestamp).toLocaleString()}</span>
                      </div>
                    }
                  >
                    <div className="bg-gray-50 p-3 rounded text-sm space-y-2">
                      <div className="break-words">
                        <strong>{t('prompt') || "Prompt"}:</strong> {item.topic}
                      </div>
                      <div className="break-all">
                        <strong>{t("link")}:</strong>{' '}
                        <a
                          href={`/tools/audio-quiz/${item.id}`}
                          className="text-indigo-600 hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {window.location.origin}/tools/audio-quiz/{item.id}
                        </a>
                      </div>
                      {analyticsMap[item.id] && (
                        <div className="overflow-x-auto mt-4">
                          <table className="min-w-full text-sm text-left border border-gray-300 rounded">
                            <caption className="text-left text-sm font-semibold mb-2">
                              {t('analytics')}
                            </caption>
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="border px-3 py-2">{t('users')}</th>
                                <th className="border px-3 py-2">{t('started')}</th>
                                <th className="border px-3 py-2">{t('completed')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border px-3 py-2">{analyticsMap[item.id].unique_users}</td>
                                <td className="border px-3 py-2">{analyticsMap[item.id].total_started}</td>
                                <td className="border px-3 py-2">{analyticsMap[item.id].total_completed}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </Collapse.Panel>
                ))}
              </Collapse>
              <StandardButton 
                type="secondary" 
                danger
                onClick={handleClearHistory} 
                className="mt-4"
              >
                {t("clear_history_button") || "Clear History"}
              </StandardButton>
            </div>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
}
